import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DownloadIcon from '@mui/icons-material/Download'
import RefreshIcon from '@mui/icons-material/Refresh'
import ReplayIcon from '@mui/icons-material/Replay'
import { shopService } from '../services'
import { UPLOAD_HISTORY_POLL_MS } from '../constants/uploadHistoryPolling'
import { formatDateTime } from '../utils/dateFormat'
import { requestedByLabel } from '../utils/exportRequestedBy'

const STATUS_STYLES = {
  queued: { bgcolor: '#e3f2fd', color: '#1565c0', label: 'Queued' },
  processing: { bgcolor: '#fff3e0', color: '#e65100', label: 'Preparing' },
  preparing: { bgcolor: '#fff3e0', color: '#e65100', label: 'Preparing' },
  completed: { bgcolor: '#e8f5e9', color: '#1b5e20', label: 'Ready' },
  ready: { bgcolor: '#e8f5e9', color: '#1b5e20', label: 'Ready' },
  failed: { bgcolor: '#ffebee', color: '#c62828', label: 'Failed' },
  expired: { bgcolor: '#eceff1', color: '#546e7a', label: 'Expired' },
}

const SHOP_STATUSES = ['active', 'pending', 'rejected', 'closed']

function retryPayloadFromJob(job) {
  const columns = Array.isArray(job.selectedColumns) ? job.selectedColumns : []
  const format = columns[1] === 'csv' ? 'csv' : job.exportFormat === 'csv' ? 'csv' : 'xlsx'
  const status = SHOP_STATUSES.includes(columns[2]) ? columns[2] : 'active'
  const search = columns[3]?.trim() || undefined
  return { format, status, search }
}

function StatusChip({ status, displayStatus }) {
  const key = displayStatus || status || 'failed'
  const style = STATUS_STYLES[key] || STATUS_STYLES.failed
  return (
    <Chip
      size="small"
      label={style.label}
      sx={{
        fontWeight: 600,
        bgcolor: style.bgcolor,
        color: style.color,
        border: 'none',
      }}
    />
  )
}

const ShopExportsPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isCompact = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const [jobs, setJobs] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [toast, setToast] = useState(null)
  const [downloadingId, setDownloadingId] = useState(null)
  const [retryingId, setRetryingId] = useState(null)

  const hasActiveJobs = useMemo(
    () => jobs.some((job) => job.status === 'queued' || job.status === 'processing'),
    [jobs],
  )

  const fetchJobs = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (!silent) setLoading(true)
        setError(null)
        const response = await shopService.getExportJobs({
          page: page + 1,
          limit: rowsPerPage,
        })
        setJobs(response.items || [])
        setTotal(response.pagination?.total || 0)
      } catch (err) {
        if (!silent) {
          setError(
            err.response?.data?.message ||
              'Could not load shop exports. Check your connection and try again.',
          )
        }
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [page, rowsPerPage],
  )

  useEffect(() => {
    fetchJobs()
  }, [fetchJobs])

  useEffect(() => {
    const queuedMessage = location.state?.queuedMessage
    if (!queuedMessage) return
    setToast(queuedMessage)
    navigate(location.pathname, { replace: true, state: {} })
  }, [location.state, location.pathname, navigate])

  useEffect(() => {
    if (!hasActiveJobs) return undefined
    const id = setInterval(() => {
      fetchJobs({ silent: true })
    }, UPLOAD_HISTORY_POLL_MS)
    return () => clearInterval(id)
  }, [hasActiveJobs, fetchJobs])

  const handleDownload = async (job) => {
    if (!job.canDownload) {
      setToast(
        job.status === 'expired'
          ? 'This file expired after 7 days. Start a new export from Shops.'
          : job.status === 'failed'
            ? job.errorMessage || 'This export failed. Start a new export from Shops.'
            : 'This file is still being prepared. Wait until status is Ready.',
      )
      return
    }

    try {
      setDownloadingId(job.id)
      const { downloadUrl, fileName } = await shopService.getExportJobUrl(job.id)
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = fileName || 'shops-export.xlsx'
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setToast('Download started.')
    } catch (err) {
      setToast(
        err.response?.data?.message ||
          'Could not download this file. Refresh and try again.',
      )
    } finally {
      setDownloadingId(null)
    }
  }

  const handleRetry = async (job) => {
    try {
      setRetryingId(job.id)
      const response = await shopService.createExport(retryPayloadFromJob(job))
      setToast(
        response?.message ||
          `Export started again${response?.jobId ? ` (Job ID ${response.jobId})` : ''}.`,
      )
      setPage(0)
      await fetchJobs()
    } catch (err) {
      setToast(
        err.response?.data?.message ||
          'Could not restart this export. Check your connection and try again.',
      )
    } finally {
      setRetryingId(null)
    }
  }

  return (
    <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%', overflowX: 'hidden' }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/shops')}
            sx={{ mb: 1, color: '#e90000' }}
          >
            Back to Shops
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
            Shop Exports
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Shared with every admin who can open Shops. Each row shows who started the export and when.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => fetchJobs()}
          sx={{ color: '#e90000', borderColor: '#e90000' }}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, wordBreak: 'break-word' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {hasActiveJobs && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Preparing files… This page updates automatically while jobs are queued or preparing.
        </Alert>
      )}

      <Paper
        variant="outlined"
        sx={{ width: '100%', maxWidth: '100%', minWidth: 0, overflow: 'hidden' }}
      >
        {loading && jobs.length === 0 ? (
          <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : jobs.length === 0 ? (
          <Box sx={{ py: 8, px: 3, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              No exports yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Start one from Shops → Export.
            </Typography>
            <Button
              component={RouterLink}
              to="/shops"
              variant="contained"
              sx={{ bgcolor: '#e90000', '&:hover': { bgcolor: '#c00000' } }}
            >
              Go to Shops
            </Button>
          </Box>
        ) : (
          <TableContainer sx={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow>
                <TableCell>Started</TableCell>
                <TableCell>Processing started</TableCell>
                <TableCell>Completed</TableCell>
                <TableCell>Requested by</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>Rows</TableCell>
                <TableCell>Format</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => (
                <TableRow key={job.id} hover>
                  <TableCell>{formatDateTime(job.createdAt)}</TableCell>
                  <TableCell>{formatDateTime(job.processingStartedAt)}</TableCell>
                  <TableCell>{formatDateTime(job.completedAt)}</TableCell>
                  <TableCell sx={{ maxWidth: 240 }}>{requestedByLabel(job)}</TableCell>
                  <TableCell>
                    <StatusChip status={job.status} displayStatus={job.displayStatus} />
                    {job.status === 'failed' && job.errorMessage && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ display: 'block', mt: 0.5, maxWidth: 280 }}
                      >
                        {job.errorMessage}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{job.search || '—'}</TableCell>
                  <TableCell>
                    {job.status === 'completed' || job.displayStatus === 'ready'
                      ? job.productCount ?? 0
                      : '—'}
                  </TableCell>
                  <TableCell>{job.exportFormat === 'csv' ? 'CSV' : 'Excel'}</TableCell>
                  <TableCell>{formatDateTime(job.expiresAt)}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      {job.canRetry && (
                        <Button
                          size="small"
                          startIcon={
                            retryingId === job.id ? <CircularProgress size={14} /> : <ReplayIcon />
                          }
                          onClick={() => handleRetry(job)}
                          disabled={retryingId === job.id}
                        >
                          Try again
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={
                          downloadingId === job.id ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <DownloadIcon />
                          )
                        }
                        disabled={!job.canDownload || downloadingId === job.id}
                        onClick={() => handleDownload(job)}
                        sx={{
                          bgcolor: '#e90000',
                          '&:hover': { bgcolor: '#c00000' },
                          '&.Mui-disabled': { bgcolor: '#f5c0c0', color: '#fff' },
                        }}
                      >
                        {isCompact
                          ? 'Download'
                          : `Download ${job.exportFormat === 'csv' ? 'CSV' : 'Excel'}`}
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </TableContainer>
        )}
        {jobs.length > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10) || 10)
              setPage(0)
            }}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage={isCompact ? 'Rows' : 'Rows per page:'}
          />
        )}
      </Paper>

      <Typography variant="body2" sx={{ mt: 2, color: '#666' }}>
        Need a new export?{' '}
        <Link component={RouterLink} to="/shops" underline="hover">
          Open Shops
        </Link>
      </Typography>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        message={toast || ''}
      />
    </Box>
  )
}

export default ShopExportsPage
